/**
 * The current version of the message protocol. This is incremented when there are significant
 * changes to the flow of messages in a session, or breaking changes to individual message
 * schemas occur, but typically not when individual message schemas are updated in backward and
 * forward compatible ways.
 */
export const currentProtocolVersion = 2;
/**
 * Server error code for streaming API
 */
export var ExecutionErrorCode;
(function (ExecutionErrorCode) {
    // Single error code covering all server related errors, e.g.,
    // TooManyRequests(429), InternalServerError(500), ServiceUnavailable(503), etc.
    // all map to this error code.
    ExecutionErrorCode[ExecutionErrorCode["ServerError"] = 0] = "ServerError";
    // Workflow
    ExecutionErrorCode[ExecutionErrorCode["WorkflowDisabled"] = 100] = "WorkflowDisabled";
    ExecutionErrorCode[ExecutionErrorCode["TokenNotReady"] = 101] = "TokenNotReady";
    ExecutionErrorCode[ExecutionErrorCode["FlightNotReady"] = 102] = "FlightNotReady";
    ExecutionErrorCode[ExecutionErrorCode["ContextNotReady"] = 103] = "ContextNotReady";
    ExecutionErrorCode[ExecutionErrorCode["WorkflowExcluded"] = 104] = "WorkflowExcluded";
    ExecutionErrorCode[ExecutionErrorCode["WorkflowExecutionTimeout"] = 105] = "WorkflowExecutionTimeout";
    ExecutionErrorCode[ExecutionErrorCode["LambdaExecutionError"] = 106] = "LambdaExecutionError";
    ExecutionErrorCode[ExecutionErrorCode["UnexpectedOutput"] = 107] = "UnexpectedOutput";
    ExecutionErrorCode[ExecutionErrorCode["FailedToFetchInputs"] = 108] = "FailedToFetchInputs";
})(ExecutionErrorCode || (ExecutionErrorCode = {}));
/**
 * Client error code for streaming API
 */
export var GetAnnotationsClientErrorCode;
(function (GetAnnotationsClientErrorCode) {
    GetAnnotationsClientErrorCode[GetAnnotationsClientErrorCode["Unknown"] = 0] = "Unknown";
    GetAnnotationsClientErrorCode[GetAnnotationsClientErrorCode["InvalidRequest"] = 1] = "InvalidRequest";
    GetAnnotationsClientErrorCode[GetAnnotationsClientErrorCode["InvalidResponse"] = 2] = "InvalidResponse";
    GetAnnotationsClientErrorCode[GetAnnotationsClientErrorCode["RuntimeNotInitialized"] = 3] = "RuntimeNotInitialized";
    GetAnnotationsClientErrorCode[GetAnnotationsClientErrorCode["RequestCancelled"] = 4] = "RequestCancelled";
    GetAnnotationsClientErrorCode[GetAnnotationsClientErrorCode["ResponseReceivedAfterFinalResponse"] = 5] = "ResponseReceivedAfterFinalResponse";
})(GetAnnotationsClientErrorCode || (GetAnnotationsClientErrorCode = {}));
export var ErrorResponseCode;
(function (ErrorResponseCode) {
    ErrorResponseCode[ErrorResponseCode["Unknown"] = 0] = "Unknown";
    // Well-known HTTP Codes
    ErrorResponseCode[ErrorResponseCode["Found"] = 302] = "Found";
    ErrorResponseCode[ErrorResponseCode["BadRequest"] = 400] = "BadRequest";
    ErrorResponseCode[ErrorResponseCode["Unauthorized"] = 401] = "Unauthorized";
    ErrorResponseCode[ErrorResponseCode["Forbidden"] = 403] = "Forbidden";
    ErrorResponseCode[ErrorResponseCode["NotFound"] = 404] = "NotFound";
    ErrorResponseCode[ErrorResponseCode["RequestTimeout"] = 408] = "RequestTimeout";
    ErrorResponseCode[ErrorResponseCode["Conflict"] = 409] = "Conflict";
    ErrorResponseCode[ErrorResponseCode["Gone"] = 410] = "Gone";
    ErrorResponseCode[ErrorResponseCode["RequestEntityTooLarge"] = 413] = "RequestEntityTooLarge";
    ErrorResponseCode[ErrorResponseCode["UnsupportedMediaType"] = 415] = "UnsupportedMediaType";
    ErrorResponseCode[ErrorResponseCode["UnprocessableContent"] = 422] = "UnprocessableContent";
    ErrorResponseCode[ErrorResponseCode["TooManyRequests"] = 429] = "TooManyRequests";
    ErrorResponseCode[ErrorResponseCode["SocketDisconnect"] = 499] = "SocketDisconnect";
    ErrorResponseCode[ErrorResponseCode["InternalServerError"] = 500] = "InternalServerError";
    ErrorResponseCode[ErrorResponseCode["ServiceUnavailable"] = 503] = "ServiceUnavailable";
    ErrorResponseCode[ErrorResponseCode["GatewayTimeout"] = 504] = "GatewayTimeout";
    // Session Protocol
    ErrorResponseCode[ErrorResponseCode["UnsupportedMessage"] = 1000] = "UnsupportedMessage";
    ErrorResponseCode[ErrorResponseCode["Cancelled"] = 1001] = "Cancelled";
    ErrorResponseCode[ErrorResponseCode["EgressError"] = 1002] = "EgressError";
    // Sync Message
    ErrorResponseCode[ErrorResponseCode["SyncMessageException"] = 2000] = "SyncMessageException";
    ErrorResponseCode[ErrorResponseCode["SyncMessageUnsupported"] = 2001] = "SyncMessageUnsupported";
    ErrorResponseCode[ErrorResponseCode["SyncMessageUnexpectedSeed"] = 2002] = "SyncMessageUnexpectedSeed";
    ErrorResponseCode[ErrorResponseCode["SyncMessageUnsupportedBatch"] = 2003] = "SyncMessageUnsupportedBatch";
    ErrorResponseCode[ErrorResponseCode["SyncMessageQueueFull"] = 2004] = "SyncMessageQueueFull";
    ErrorResponseCode[ErrorResponseCode["SyncMessageTooLateOrDuplicate"] = 2005] = "SyncMessageTooLateOrDuplicate";
    ErrorResponseCode[ErrorResponseCode["SyncMessageGroupIdMismatch"] = 2006] = "SyncMessageGroupIdMismatch";
    ErrorResponseCode[ErrorResponseCode["SyncMessageGroupStop"] = 2007] = "SyncMessageGroupStop";
    ErrorResponseCode[ErrorResponseCode["SyncMessageLost"] = 2008] = "SyncMessageLost";
    ErrorResponseCode[ErrorResponseCode["SyncMessageUnprocessedDuplicate"] = 2009] = "SyncMessageUnprocessedDuplicate";
    ErrorResponseCode[ErrorResponseCode["SyncMessageSessionClosed"] = 2010] = "SyncMessageSessionClosed";
    ErrorResponseCode[ErrorResponseCode["SyncMessageAbandoned"] = 2011] = "SyncMessageAbandoned";
    ErrorResponseCode[ErrorResponseCode["SyncMessageTooManyDeltaOperations"] = 2012] = "SyncMessageTooManyDeltaOperations";
    ErrorResponseCode[ErrorResponseCode["SyncMessageSessionSizeLimitExceeded"] = 2013] = "SyncMessageSessionSizeLimitExceeded";
    // TokenProvision
    ErrorResponseCode[ErrorResponseCode["TokenValidationError"] = 2100] = "TokenValidationError";
    ErrorResponseCode[ErrorResponseCode["TokenDecryptError"] = 2101] = "TokenDecryptError";
    ErrorResponseCode[ErrorResponseCode["TokenTypeError"] = 2102] = "TokenTypeError";
    ErrorResponseCode[ErrorResponseCode["TokenUserBlocked"] = 2103] = "TokenUserBlocked";
    // AnnotationActivation
    ErrorResponseCode[ErrorResponseCode["AnnotationActivationInvalidType"] = 2200] = "AnnotationActivationInvalidType";
    // AnnotationRelease
    ErrorResponseCode[ErrorResponseCode["AnnotationReleaseTokenNotFound"] = 2300] = "AnnotationReleaseTokenNotFound";
})(ErrorResponseCode || (ErrorResponseCode = {}));
/**
 * Session LongPoll message type.
 */
export var SessionLongPollMessageType;
(function (SessionLongPollMessageType) {
    SessionLongPollMessageType[SessionLongPollMessageType["UnKnown"] = 0] = "UnKnown";
    SessionLongPollMessageType[SessionLongPollMessageType["Start"] = 1] = "Start";
    SessionLongPollMessageType[SessionLongPollMessageType["Regular"] = 2] = "Regular";
    SessionLongPollMessageType[SessionLongPollMessageType["CheckConnection"] = 3] = "CheckConnection";
    SessionLongPollMessageType[SessionLongPollMessageType["PostEgress"] = 4] = "PostEgress";
    /**
     * Timeout resend is a special fail resend.
     */
    SessionLongPollMessageType[SessionLongPollMessageType["TimeoutResend"] = 5] = "TimeoutResend";
    SessionLongPollMessageType[SessionLongPollMessageType["FailResend"] = 6] = "FailResend";
})(SessionLongPollMessageType || (SessionLongPollMessageType = {}));
export var SessionSwapReason;
(function (SessionSwapReason) {
    SessionSwapReason[SessionSwapReason["IdentityChange"] = 0] = "IdentityChange";
})(SessionSwapReason || (SessionSwapReason = {}));
export var AnnotationResultState;
(function (AnnotationResultState) {
    /**
     * No workflows are currently running and no annotation results will be produced until
     * additional SyncMessages are received.
     */
    AnnotationResultState[AnnotationResultState["Idle"] = 0] = "Idle";
    /**
     * Workflows are currently running and/or the client has just activated the annotation type;
     * more results may arrive. This is the default state after the first activation of a given
     * annotation type.
     */
    AnnotationResultState[AnnotationResultState["Pending"] = 1] = "Pending";
})(AnnotationResultState || (AnnotationResultState = {}));
/**
 * This is the seeding status.
 */
export var SeedingProgressCode;
(function (SeedingProgressCode) {
    /**
     * The default status when the seeding has not started.
     * The client will not receive this status because it is the default status.
     */
    SeedingProgressCode[SeedingProgressCode["NotStarted"] = 0] = "NotStarted";
    /**
     * The seeding is stared and in progress.
     * The client will receive this status when the server handles the first seeding message.
     */
    SeedingProgressCode[SeedingProgressCode["Started"] = 1] = "Started";
    /**
     * The seeding has not completed successfully.
     * The client will receive this status when the server finishes the 'seeding' but some seeding messages are missing.
     */
    SeedingProgressCode[SeedingProgressCode["Incomplete"] = 2] = "Incomplete";
    /**
     * The seeding has completed successfully.
     * The client will receive this status when the server handles all seeding messages.
     */
    SeedingProgressCode[SeedingProgressCode["Finished"] = 3] = "Finished";
})(SeedingProgressCode || (SeedingProgressCode = {}));
//# sourceMappingURL=messages.js.map